import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import { Form, FormGroup, FormLabel } from '@/components/ui/Form';
import type { ConstructionBoqItem } from '../types/construction-boq.types';

interface BoqItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<ConstructionBoqItem>) => Promise<void>;
  item?: ConstructionBoqItem | null;
  boqId: string;
  projectId: string;
  companyId: string;
}

export function BoqItemFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  item, 
  boqId, 
  projectId, 
  companyId 
}: BoqItemFormModalProps) {
  const [formData, setFormData] = useState<Partial<ConstructionBoqItem>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData({ ...item });
      } else {
        setFormData({
          boq_id: boqId,
          project_id: projectId,
          company_id: companyId,
          item_code: '',
          item_name: '',
          unit: '',
          quantity: 0,
          unit_price: 0,
          total_amount: 0,
          material_cost: 0,
          labor_cost: 0,
          equipment_cost: 0,
          subcontract_cost: 0,
          category_code: 'general',
          status_code: 'planned',
          sort_order: 0
        });
      }
    }
  }, [isOpen, item, boqId, projectId, companyId]);

  // Auto calculate total amount
  useEffect(() => {
    const qty = formData.quantity || 0;
    const price = formData.unit_price || 0;
    setFormData(prev => ({ ...prev, total_amount: qty * price }));
  }, [formData.quantity, formData.unit_price]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={item ? 'Cập nhật hạng mục BOQ' : 'Thêm hạng mục BOQ mới'} 
      size="lg"
    >
      <Form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormGroup>
            <FormLabel required>Mã hạng mục</FormLabel>
            <Input 
              value={formData.item_code || ''} 
              onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
              placeholder="VD: C.01.01"
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel required>Tên hạng mục/Công việc</FormLabel>
            <Input 
              value={formData.item_name || ''} 
              onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              placeholder="VD: Đào móng hố trụ"
              required
            />
          </FormGroup>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormGroup>
            <FormLabel required>Đơn vị tính</FormLabel>
            <Input 
              value={formData.unit || ''} 
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              placeholder="VD: m3, m2, cái..."
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel required>Số lượng</FormLabel>
            <Input 
              type="number"
              value={formData.quantity || ''} 
              onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
              required
            />
          </FormGroup>
          <FormGroup>
            <FormLabel required>Đơn giá (VNĐ)</FormLabel>
            <Input 
              type="number"
              value={formData.unit_price || ''} 
              onChange={(e) => setFormData({ ...formData, unit_price: Number(e.target.value) })}
              required
            />
          </FormGroup>
        </div>

        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex justify-between items-center">
          <span className="text-sm font-semibold text-slate-600">Thành tiền dự kiến:</span>
          <span className="text-lg font-bold text-os-primary">
            {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(formData.total_amount || 0)}
          </span>
        </div>

        <FormGroup>
          <FormLabel>Thông số kỹ thuật / Ghi chú</FormLabel>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[80px]"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Mô tả chi tiết kỹ thuật hoặc yêu cầu thi công..."
          />
        </FormGroup>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            {item ? 'Lưu thay đổi' : 'Thêm hạng mục'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
